import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import App from './App';
import {json, RouterProvider} from 'react-router';
import {createBrowserRouter} from 'react-router-dom';
import { QueryErrorBoundary } from './components/QueryErrorBoundary';
import { SuspenseFallback } from './components/SuspenseFallback';
import DefaultError from './routes/default-error';
import {NetworkCalls} from './NetworkCalls';
import axios from 'axios';
import {Organisation} from './dtos/Organisation';
import {OrganisationProvider} from "./custom-hooks/useOrganisation";

// Lazy-loaded route components for code splitting
const Auth = lazy(() => import('./Auth'));
const SelectOrganisation = lazy(() => import('./login-area/SelectOrganisation'));
const Overview = lazy(() => import('./login-area/Overview'));
const UploadMemberList = lazy(() => import('./login-area/administration/UploadMemberList'));
const MemberList = lazy(() => import('./login-area/administration/MemberList'));
const MemberDetails = lazy(() => import('./login-area/administration/MemberDetails'));
const MemberDetailsEdit = lazy(() => import('./login-area/administration/MemberDetailsEdit'));
const InspectionAreaOverview = lazy(() => import('./login-area/inspection-area/InspectionAreaOverview'));
const InspectionDetail = lazy(() => import('./login-area/inspection-area/InspectionDetail'));
const CatchReports = lazy(() => import('./login-area/catch-reports/CatchReports'));
const GuestCardPaymentForm = lazy(() => import('./guest-cards/GuestCardPaymentForm'));
const CheckoutOverview = lazy(() => import('./guest-cards/CheckoutOverview'));
const PaymentSuccessful = lazy(() => import('./guest-cards/PaymentSuccessful'));
const Payments = lazy(() => import('./login-area/guest-cards/Payments'));
const GuestCardOverview = lazy(() => import('./guest-cards/GuestCardOverview'));
const EditCatchYear = lazy(() => import('./login-area/catch-reports/EditCatchYear'));
const FeatureConfiguration = lazy(() => import('./login-area/fangkarte-admin/FeatureConfiguration'));
const CatchReport = lazy(() => import('./catch-report/CatchReport'));
const CatchReportMemberPage = lazy(() => import('./catch-report/CatchReportMemberPage'));
const CatchReportGuestCardPage = lazy(() => import('./catch-report/CatchReportGuestCardPage'));
const UserList = lazy(() => import('./login-area/administration/user/UserList'));
const MemberWork = lazy(() => import('./login-area/member-work/MemberWork'));
const ActivateEmailVerification = lazy(() => import('./activate/ActivateEmailVerification'));
const MemberWorkExtended = lazy(() => import('./login-area/member-work-extended/MemberWorkExtended'));
const MemberWorkReport = lazy(() => import('./login-area/reports/MemberWorkReport'));
const ForgotPassword = lazy(() => import('./auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./auth/ResetPassword'));
const WaterListOverview = lazy(() => import('./login-area/water-administration/WaterListOverview'));
const AppointmentCategories = lazy(() => import('./login-area/fangkarte-admin/AppointmentCategories'));
const Appointments = lazy(() => import('./login-area/administration/Appointments'));
const News = lazy(() => import('./login-area/administration/News'));
const GuestCardAdmin = lazy(() => import('./login-area/guest-cards/GuestCardAdmin'));
const AddGuestCard = lazy(() => import('./login-area/guest-cards/AddGuestCard'));
const DocumentManagement = lazy(() => import('./login-area/administration/document-management/DocumentManagement'));
const CatchReportCharts = lazy(() => import('./login-area/reports/CatchReportCharts').then(module => ({ default: module.CatchReportCharts })));
const EditGuestCard = lazy(() => import('./login-area/guest-cards/EditGuestCard').then(module => ({ default: module.EditGuestCard })));
const FishProtectionsView = lazy(() => import('./login-area/water-administration/FishProtectionsView'));
const EditCatchReportView = lazy(() => import('./login-area/catch-reports/EditCatchReportView'));
const DashboardHome = lazy(() => import('./login-area/DashboardHome'));
const Settings = lazy(() => import('./login-area/Settings'));
const OrganisationManagement = lazy(() => import('./login-area/administration/OrganisationManagement'));

axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;
axios.defaults.withCredentials = true;

document.body.style.removeProperty('background');

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

let organisations: Organisation[] = [];

function getOrganisationTokenLoader() {
  return async ({params}: {params: any}) => {
    try {
      if (!organisations[params.organisationToken]) {
        organisations[params.organisationToken] =
          await NetworkCalls.getOrganisationByToken(params.organisationToken);
      }

      return json(organisations[params.organisationToken]);
    } catch (e) {
      throw new Response('Organisation nicht gefunden', {status: 404});
    }
  };
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <DefaultError />,
    children: [
      {
        path: 'guest-card/:organisationId',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Gastkarten-Formular..." />}>
              <GuestCardPaymentForm />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'guest-card/',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Gastkarten..." />}>
              <GuestCardOverview />
            </Suspense>
          </QueryErrorBoundary>
        ),
        children: [
          // TODO
        ]
      },
      {
        path: 'catch-report/:organisationToken',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Fangmeldung..." />}>
              <CatchReport />
            </Suspense>
          </QueryErrorBoundary>
        ),
        loader: getOrganisationTokenLoader()
      },
      {
        path: 'catch-report/:organisationToken/member',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Fangmeldung..." />}>
              <CatchReportMemberPage />
            </Suspense>
          </QueryErrorBoundary>
        ),
        loader: getOrganisationTokenLoader()
      },
      {
        path: 'catch-report/:organisationToken/guest-card/:guestCardToken',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Fangmeldung..." />}>
              <CatchReportGuestCardPage />
            </Suspense>
          </QueryErrorBoundary>
        ),
        loader: getOrganisationTokenLoader()
      },
      {
        path: 'checkout',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Checkout..." />}>
              <CheckoutOverview />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'activate/:activationToken',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Aktivierung..." />}>
              <ActivateEmailVerification />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'payment-successful',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade..." />}>
              <PaymentSuccessful />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'login',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Login..." />}>
              <Auth />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'forgot-password',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade..." />}>
              <ForgotPassword />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'reset-password',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade..." />}>
              <ResetPassword />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'reset-password/:resetToken',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade..." />}>
              <ResetPassword />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'select-organisation',
        element: (
          <QueryErrorBoundary>
            <Suspense fallback={<SuspenseFallback type="page" message="Lade Organisationen..." />}>
              <SelectOrganisation />
            </Suspense>
          </QueryErrorBoundary>
        )
      },
      {
        path: 'dashboard',
        element: (
          <OrganisationProvider>
            <QueryErrorBoundary>
              <Suspense fallback={<SuspenseFallback type="page" message="Lade Dashboard..." />}>
                <Overview />
              </Suspense>
            </QueryErrorBoundary>
          </OrganisationProvider>
        ),
        children: [
          {
            index: true,
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Dashboard..." />}>
                  <DashboardHome />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/member-list/upload',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Upload..." />}>
                  <UploadMemberList />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/member-list',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Mitgliederliste..." />}>
                  <MemberList />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/member-list/:memberId',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Mitglied..." />}>
                  <MemberDetails />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/member-list/:memberId/edit',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Mitgliedsdaten..." />}>
                  <MemberDetailsEdit />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/guest-cards',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Gastkarten..." />}>
                  <GuestCardAdmin />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/guest-cards/add',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Gastkarten-Formular..." />}>
                  <AddGuestCard />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/guest-cards/edit/:cardId',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Gastkarte..." />}>
                  <EditGuestCard />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'termine/verwalten',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Termine..." />}>
                  <Appointments />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'news/verwalten',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade News..." />}>
                  <News />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'water-administration/water-list',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Gewässer..." />}>
                  <WaterListOverview />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: "water-administration/protections",
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Schonzeiten..." />}>
                  <FishProtectionsView />
                </Suspense>
              </QueryErrorBoundary>
            ),
          },
          {
            path: 'inspections',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Kontrollen..." />}>
                  <InspectionAreaOverview />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'inspections/details/:id',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Kontrolldetails..." />}>
                  <InspectionDetail />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'catch-report/catch-year',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Fangjahre..." />}>
                  <EditCatchYear />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'catch-report/list',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Fangberichte..." />}>
                  <CatchReports />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'catch-report/edit/:catchReportId',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Fangmeldung..." />}>
                  <EditCatchReportView />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'guest-cards/payments',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Zahlungen..." />}>
                  <Payments />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration-area/feature-configuration',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="page" message="Lade Features..." />}>
                  <FeatureConfiguration />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration-area/organisation-categories',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="page" message="Lade Kategorien..." />}>
                  <AppointmentCategories />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/organisations',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Organisationen..." />}>
                  <OrganisationManagement />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'settings',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Einstellungen..." />}>
                  <Settings />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'administration/user/user-list',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Benutzerliste..." />}>
                  <UserList />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'member-work',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Arbeitsdienst..." />}>
                  <MemberWork />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'member-work-extended',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Arbeitsdienst..." />}>
                  <MemberWorkExtended />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'member-work-report',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Bericht..." />}>
                  <MemberWorkReport />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'catch-reports',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Statistiken..." />}>
                  <CatchReportCharts />
                </Suspense>
              </QueryErrorBoundary>
            )
          },
          {
            path: 'dokumentenverwaltung',
            element: (
              <QueryErrorBoundary>
                <Suspense fallback={<SuspenseFallback type="content" message="Lade Dokumente..." />}>
                  <DocumentManagement />
                </Suspense>
              </QueryErrorBoundary>
            )
          }
        ]
      }
    ]
  }
]);

root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
